import type { CSSProperties } from 'react';

/**
 * HITL-1613 — the cursor must not promise an action the current mode refuses.
 *
 * Applied to the surfaces whose click is refused outside `Intercept`
 * (`selectIsAgentActionBlocked`): the POS cart lines, the subtotal / total
 * lines, the cart card's action row and the centre pane's description tab.
 * Every one of those actions is already gated — HITL-1606 at the send hooks
 * and HITL-1612 at the three sinks — so this changes nothing but the look.
 *
 * Deliberately a module-level INLINE style rather than a `makeStyles` rule or
 * an `sx` value:
 *
 * - `makeStyles` numbers its class names off a global counter, so adding a
 *   rule renumbers every sheet created after it and shows up as diff noise in
 *   unrelated snapshots. A constant adds no rule.
 * - `sx` is the same hazard one layer down: a new key changes the emotion hash
 *   whether or not the mode is silent.
 * - An inline style also beats the `:hover { cursor: pointer }` rules it has to
 *   override, which a same-specificity class could not.
 *
 * A frozen object shared by every call site, so the flag-off render passes
 * `undefined` and emits no `style` attribute at all — byte-identical.
 */
export const BLOCKED_AFFORDANCE_STYLE: CSSProperties = Object.freeze({
  cursor: 'not-allowed',
});

/**
 * The reason, for the ONE place it is allowed to appear: the tooltip the
 * grouped intent pills already wrap every button in.
 *
 * The frozen design states the mode once, in the header switch — no banners, no
 * strips, no per-control copy. This rides in an existing `<Tooltip>` (whose
 * `<span>` wrapper is there because MUI tooltips need a non-disabled host) and
 * replaces the `Alt+N` hint, which would otherwise be a second lie: HITL-1606
 * refuses the Alt+digit fast-path in exactly the modes that disable the pill.
 */
export const BLOCKED_AFFORDANCE_HINT = 'Switch to Intercept to use this';
